import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
// components
//
import Login from '../pages/auth/LoginPage';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'src/redux/store';
import { postRefreshToken } from 'src/redux/slices/auth';
import { PATH_PAGE } from 'src/routes/paths';

// ----------------------------------------------------------------------

type AuthGuardProps = {
  children: React.ReactNode;
};

type RoutePermission = {
  path: string;
  roles: string[];
  match?: 'exact' | 'prefix';
};

// Aqui você define quais permissões cada rota precisa
const routePermissions: RoutePermission[] = [
  { path: '/dashboard/schedule', roles: ['scheduleCalendar'], match: 'exact' },
  { path: '/dashboard/delivery/orders', roles: ['restaurantOrders'], match: 'exact' },
  { path: '/dashboard/delivery/dashboard', roles: ['restaurantDashboard'], match: 'exact' },
  { path: '/dashboard/wallet', roles: ['scheduleCalendar', 'restaurantDashboard'], match: 'prefix' },
  { path: '/dashboard/instance', roles: ['whatsapp'], match: 'exact' },
  { path: '/dashboard/chats', roles: ['chats'], match: 'exact' },
  { path: '/dashboard/contacts', roles: ['contacts'], match: 'exact' },
  { path: '/dashboard/knowledge-base', roles: ['knowledgeBase'], match: 'exact' },
  { path: '/dashboard/alfred-studio', roles: ['assistantConfig'], match: 'exact' },
  { path: '/dashboard/broadcasts/campaign', roles: ['broadcastsCampaignList'], match: 'exact' },
  { path: '/dashboard/broadcasts/campaign/new', roles: ['broadcastsCampaignNew'], match: 'exact' },
  { path: '/dashboard/broadcasts/campaign/', roles: ['broadcastsCampaignEdit'], match: 'prefix' },
  { path: '/dashboard/broadcasts/followup', roles: ['broadcastsFollowUpList'], match: 'exact' },
  { path: '/dashboard/broadcasts/followup/new', roles: ['broadcastsFollowUpNew'], match: 'exact' },
  { path: '/dashboard/broadcasts/followup/', roles: ['broadcastsFollowUpEdit'], match: 'prefix' },
  // adicione as outras rotas que precisar
];

const getRequiredRoles = (pathname: string) => {
  const exact = routePermissions.find(
    (item) => (item.match ?? 'exact') === 'exact' && item.path === pathname
  );
  if (exact) return exact.roles;

  const prefix = routePermissions.find(
    (item) => item.match === 'prefix' && pathname.startsWith(item.path)
  );
  if (prefix) return prefix.roles;

  return [];
};

export default function AuthGuard({ children }: AuthGuardProps) {
  const authState = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [requestedLocation, setRequestedLocation] = useState<string | null>(null);

  // Refresh token
  useEffect(() => {
    if (authState.refreshToken) {
      dispatch(postRefreshToken({ refreshToken: authState.refreshToken }));
    }
  }, [dispatch, authState.refreshToken]);

  // Verifica se o usuário está logado
  if (!authState.accessToken || authState.accessToken.length === 0) {
    if (pathname !== requestedLocation) setRequestedLocation(pathname);
    return <Login />;
  }

  // Bloqueia rota de usuários para subusuário
  if (pathname.startsWith('/dashboard/users') && authState.userReturn.isSubUser) {
    return <Navigate to={PATH_PAGE.page403} />;
  }

  if (pathname.startsWith('/dashboard/wallet') && authState.userReturn.isSubUser) {
    return <Navigate to={PATH_PAGE.page403} />;
  }

  if (pathname === '/dashboard/user/integrations' && authState.userReturn.isSubUser) {
    return <Navigate to={PATH_PAGE.page403} />;
  }

  // Verifica permissões para a rota atual
  const requiredRoles = getRequiredRoles(pathname);
  const userRoles: string[] =
    authState.userReturn.modules?.map((m: any) =>
      typeof m === 'string' ? m.toLowerCase() : m.name?.toLowerCase()
    ) || [];

  const hasPermission =
    requiredRoles.length === 0 ||
    requiredRoles.some(role => userRoles.includes(role.toLowerCase()));

  if (!hasPermission) {
    return <Navigate to={PATH_PAGE.page403} />;
  }

  // Redireciona se necessário
  if (requestedLocation && pathname !== requestedLocation) {
    setRequestedLocation(null);
    return <Navigate to={requestedLocation} />;
  }

  return <>{children}</>;
}
